// ═══════════════════════════════════════════════════════════════
// Détection d'une nouvelle version déployée (évite le hard-refresh manuel)
// ═══════════════════════════════════════════════════════════════
// Le front connaît SA version de build (__APP_VERSION__, figée à la compilation).
// Un déploiement met à jour le BACK puis le FRONT (pas simultanément). Pour ne PAS
// proposer le rechargement quand seul le back est à jour (le nouveau front n'étant
// pas encore servi → boucle de rechargement), on croise DEUX témoins :
//   - GET /api/version        → version du backend déployé
//   - GET /version.json       → version du front réellement servi (émis au build)
// On ne propose la maj que si les DEUX sont sur la MÊME nouvelle version, différente
// de celle chargée dans l'onglet → les deux côtés sont bien déployés.
// À appeler UNE fois (dans App.vue). État partagé au niveau module.

import { ref } from 'vue';
import { api } from '@/lib/api';

/* global __APP_VERSION__ */
const BUILD_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

const updateAvailable = ref(false);
const latestVersion = ref(BUILD_VERSION);
let started = false;

// Version du front réellement déployé (dist/version.json), avec cache-buster.
async function fetchFrontVersion() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.version || null;
  } catch {
    return null;
  }
}

async function check() {
  try {
    const [backRes, frontV] = await Promise.all([
      api.get('/version').then((r) => r?.data?.version || null).catch(() => null),
      fetchFrontVersion(),
    ]);
    const backV = backRes;
    // Maj proposée seulement si back ET front sont déployés sur la MÊME nouvelle
    // version, ≠ de celle chargée. Si version.json est absent (dev, ou front pas
    // encore servi), frontV = null → pas de modale.
    if (backV && frontV && backV === frontV && frontV !== BUILD_VERSION) {
      latestVersion.value = frontV;
      updateAvailable.value = true;
    }
  } catch {
    // Réseau indispo / endpoint absent : on reste silencieux (pas de modale).
  }
}

// HARD REFRESH : on ne se contente pas de reload() (qui peut resservir le cache).
// On vide les caches applicatifs + désenregistre les service workers, puis on
// recharge avec un paramètre cache-buster jetable → le navigateur re-télécharge
// l'index et les nouveaux assets hashés au lieu de servir la version en cache.
async function hardReload() {
  try {
    if (window.caches?.keys) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
    if (navigator.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // best effort — on recharge quand même.
  }
  const url = new URL(window.location.href);
  url.searchParams.set('_r', String(Date.now()));
  window.location.replace(url.toString());
}

export function useAppVersion() {
  if (!started) {
    started = true;
    // Retire le cache-buster jetable laissé par un hard-refresh précédent.
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.has('_r')) {
        u.searchParams.delete('_r');
        window.history.replaceState({}, '', u.toString());
      }
    } catch { /* ignore */ }
    check();
    // Poll toutes les 5 min + à chaque retour sur l'onglet.
    setInterval(check, 5 * 60 * 1000);
    window.addEventListener('focus', check);
  }
  return { updateAvailable, latestVersion, buildVersion: BUILD_VERSION, reload: hardReload };
}
